'use client';

import classNames from 'classnames';
import { usePathname } from 'next/navigation';
import { type ReactNode, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import {
  FiBookOpen,
  FiFolder,
  FiBriefcase,
  FiCalendar,
  FiChevronLeft,

  FiChevronRight,
  FiUserCheck,
  FiUsers,
} from 'react-icons/fi';

import Link from '~/shared/components/link';
import StudentsAttentionBadge from '~/shared/components/students-attention-badge';
import {
  getStoredPlatformSidebarCollapsed,
  PLATFORM_SIDEBAR_COLLAPSED_COOKIE_KEY,
  PLATFORM_SIDEBAR_COLLAPSED_STORAGE_KEY,
} from '~/shared/configs/ui-preferences';
import { type Locale, PAGE_NAMES, USER_ROLE } from '~/shared/constants';
import { getLocaleLink } from '~/shared/helpers/browser-helpers';
import { useTypedTranslation } from '~/shared/hooks';

import './platform-sidebar.scss';

const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

type PlatformSidebarUser = Readonly<{
  role?: string | null;
}> | null;

type PlatformSidebarProps = Readonly<{
  lng: Locale;
  user: PlatformSidebarUser;
  isHeaderVisible?: boolean;
}>;

type SidebarItem = Readonly<{
  key: string;
  href: string;
  label: string;
  icon: ReactNode;
  activePaths?: string[];
  excludePaths?: string[];
}>;

const PlatformSidebar = ({ lng, user, isHeaderVisible = true }: PlatformSidebarProps) => {
  const pathname = usePathname();
  const t = useTypedTranslation(['page', 'text']);

  useLayoutEffect(() => {
    const applyPersistedCollapsedState = () => {
      document.documentElement.dataset.platformSidebarCollapsed = getStoredPlatformSidebarCollapsed()
        ? 'true'
        : 'false';
    };

    applyPersistedCollapsedState();

    const onStorage = (event: StorageEvent) => {
      if (event.key !== PLATFORM_SIDEBAR_COLLAPSED_STORAGE_KEY) return;
      applyPersistedCollapsedState();
    };

    window.addEventListener('storage', onStorage);

    return () => {
      window.removeEventListener('storage', onStorage);
    };
  }, []);
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const items = useMemo<SidebarItem[]>(() => {
    const role = isHydrated ? user?.role : null;
    const result: SidebarItem[] = [];

    if (
      role === USER_ROLE.SUPER_TEACHER ||
      role === USER_ROLE.TEACHER ||
      role === USER_ROLE.ADMIN ||
      role === USER_ROLE.STUDENT
    ) {
      result.push({
        key: 'courses',
        href: getLocaleLink(PAGE_NAMES.PLATFORM_COURSES, lng),
        label: t('page.platform.courses.menu-item'),
        icon: <FiBookOpen />,
        activePaths: [
          getLocaleLink(PAGE_NAMES.PLATFORM_COURSES, lng),
          getLocaleLink(PAGE_NAMES.PLATFORM_VIDEO_COURSES, lng),
          getLocaleLink(PAGE_NAMES.PLATFORM_ONLINE_COURSE_WITHOUT_ALIAS, lng),
          getLocaleLink(PAGE_NAMES.PLATFORM_VIDEO_COURSE_WITHOUT_ALIAS, lng),
        ],
      });
    }

    if (role === USER_ROLE.SUPER_TEACHER || role === USER_ROLE.TEACHER || role === USER_ROLE.STUDENT) {
      result.push({
        key: 'calendar',
        href: getLocaleLink(PAGE_NAMES.PLATFORM_CALENDAR, lng),
        label: t('page.platform.calendar.menu-item'),
        icon: <FiCalendar />,
      });
    }

    if (role === USER_ROLE.SUPER_TEACHER || role === USER_ROLE.TEACHER) {
      result.push({
        key: 'students',
        href: getLocaleLink(PAGE_NAMES.PLATFORM_STUDENTS_BOARD, lng),
        label: t('page.platform.students-board.menu-item'),
        icon: <FiUsers />,
        activePaths: [
          getLocaleLink(PAGE_NAMES.PLATFORM_STUDENTS_BOARD, lng),
          getLocaleLink(PAGE_NAMES.PLATFORM_STUDENTS_LIST, lng),
        ],
      });
    }

    if (role === USER_ROLE.SUPER_TEACHER) {
      result.push(
        {
          key: 'teachers',
          href: getLocaleLink(PAGE_NAMES.PLATFORM_TEACHERS, lng),
          label: t('page.platform.teachers.menu-item'),
          icon: <FiUserCheck />,
        },
        {
          key: 'managers',
          href: getLocaleLink(PAGE_NAMES.PLATFORM_MANAGERS, lng),
          label: t('page.platform.managers.menu-item'),
          icon: <FiBriefcase />,
        },
      );
    }

    if (role === USER_ROLE.MANAGER) {
      result.push({
        key: 'students-list',
        href: getLocaleLink(PAGE_NAMES.PLATFORM_STUDENTS_LIST, lng),
        label: t('page.platform.students-list.menu-item'),
        icon: <FiUsers />,
      });
    }


    if (role === USER_ROLE.TEACHER || role === USER_ROLE.SUPER_TEACHER) {
      result.push({ key: 'materials', href: getLocaleLink('/platform/materials', lng),
        label: t('text.materials-library'), icon: <FiFolder /> });
    }
    return result;
  }, [lng, t, user?.role, isHydrated]);


  const isActive = (item: SidebarItem): boolean => {
    if (item.excludePaths?.some((path) => pathname.startsWith(path))) return false;
    if (item.activePaths?.length) return item.activePaths.some((path) => pathname.startsWith(path));

    return pathname === item.href || pathname.startsWith(`${item.href}/`);
  };

  const toggleCollapsed = () => {
    const root = document.documentElement;
    const next = root.dataset.platformSidebarCollapsed !== 'true';
    const value = next ? '1' : '0';

    // The DOM structure never changes between SSR and hydration.
    // Only this root attribute controls the visual collapsed state through CSS.
    root.dataset.platformSidebarCollapsed = next ? 'true' : 'false';

    try {
      window.localStorage.setItem(PLATFORM_SIDEBAR_COLLAPSED_STORAGE_KEY, value);
    } catch {
      // Cookie below remains a fallback when localStorage is unavailable.
    }

    document.cookie = `${PLATFORM_SIDEBAR_COLLAPSED_COOKIE_KEY}=${value}; Path=/; Max-Age=${SIDEBAR_COOKIE_MAX_AGE}; SameSite=Lax`;
  };

  return (
    <aside
      className={classNames('platform-sidebar', {
        'platform-sidebar--without-header': !isHeaderVisible,
      })}
      aria-label="Platform navigation"
    >
      <div className="platform-sidebar__top-bar">
        <span className="platform-sidebar__caption">PRANA CLUB</span>
        <button
          type="button"
          className="platform-sidebar__collapse-button"
          onClick={toggleCollapsed}
          aria-label={t('text.navigation-panel')}
          data-tooltip={t('text.navigation-panel')}
        >
          <FiChevronRight className="platform-sidebar__collapse-icon platform-sidebar__collapse-icon--expand" />
          <FiChevronLeft className="platform-sidebar__collapse-icon platform-sidebar__collapse-icon--collapse" />
        </button>
      </div>

      <nav className="platform-sidebar__nav">
        {items.map((item) => {
          const link = (
            <Link
              href={item.href}
              className={classNames('platform-sidebar__item', {
                'platform-sidebar__item--active': isActive(item),
              })}
              aria-current={isActive(item) ? 'page' : undefined}
            >
              <span className="platform-sidebar__icon">{item.icon}</span>
              <span className="platform-sidebar__label">{item.label}</span>
              {item.key === 'students' && (
                <StudentsAttentionBadge className="platform-sidebar__students-attention-badge" />
              )}
            </Link>
          );

          return (
            <div key={item.key} className="platform-sidebar__item-shell" data-tooltip={item.label}>
              {link}
            </div>
          );
        })}
      </nav>

      <div className="platform-sidebar__glow" aria-hidden="true" />
    </aside>
  );
};

export default PlatformSidebar;
